// apps/web/src/lib/push.ts
//
// APNs registration for the native app.
//
// Permission is requested lazily, at the moment it buys the user something —
// starting a screenshot read that takes a minute — rather than on launch. iOS
// grants exactly one prompt per install: spend it on a question the user can see
// the point of, or it gets denied and the feature is dead for good.
//
// Every export is a no-op in the browser, so callers never have to branch.

import { isNativeApp } from './appPlatform';
import { authFetch } from './authFetch';
import { API_BASE } from './apiBase';

type PushPlugin = typeof import('@capacitor/push-notifications')['PushNotifications'];

/** Payload the API attaches to a completed-import notification. */
export type PushData = { type?: string; jobId?: string };

async function plugin(): Promise<PushPlugin | null> {
  if (!isNativeApp()) return null;
  try {
    const { PushNotifications } = await import('@capacitor/push-notifications');
    return PushNotifications;
  } catch {
    // An older installed binary that predates the plugin. Not an error worth
    // surfacing — the app simply cannot notify.
    return null;
  }
}

let registered = false;
let inFlight: Promise<boolean> | null = null;

/**
 * Make sure this device is registered to receive notifications, asking for
 * permission if it has not been asked before.
 *
 * Resolves `false` for every ordinary reason it cannot happen — browser, denied,
 * APNs unconfigured server-side, no token issued — so the caller can carry on.
 * Registration is the nice-to-have; the work it announces does not depend on it.
 */
export async function ensurePushRegistered(): Promise<boolean> {
  if (registered) return true;
  // Concurrent callers (two reads in a row) must not each fire a prompt.
  if (inFlight) return inFlight;

  inFlight = (async () => {
    const push = await plugin();
    if (!push) return false;
    try {
      // Nothing to gain from a permission prompt the server cannot act on.
      const status = await authFetch(`${API_BASE}/api/push/status`).catch(() => null);
      if (status?.ok) {
        const body = await status.json().catch(() => null);
        if (body && body.configured === false) return false;
      }

      let perm = await push.checkPermissions();
      if (perm.receive === 'prompt' || perm.receive === 'prompt-with-rationale') {
        perm = await push.requestPermissions();
      }
      if (perm.receive !== 'granted') return false;

      // The token arrives through an event, not a return value, so the listener
      // has to be attached before register() is called.
      const token = await new Promise<string | null>((resolve) => {
        let done = false;
        const settle = (value: string | null) => {
          if (done) return;
          done = true;
          resolve(value);
        };
        void push.addListener('registration', (t) => settle(t?.value || null));
        void push.addListener('registrationError', (e) => {
          console.error('[push] APNs registration failed:', e);
          settle(null);
        });
        void push.register().catch((e) => {
          console.error('[push] register() threw:', e);
          settle(null);
        });
        // Airplane mode leaves register() hanging rather than failing, and a
        // promise that never settles would block every later caller.
        setTimeout(() => settle(null), 15_000);
      });
      if (!token) return false;

      const res = await authFetch(`${API_BASE}/api/push/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, platform: 'ios' }),
      });
      if (!res.ok) return false;

      registered = true;
      return true;
    } catch (e) {
      console.error('[push] registration error:', e);
      return false;
    } finally {
      inFlight = null;
    }
  })();

  return inFlight;
}

/**
 * Hand the device token back on sign-out, so the next person to use this phone
 * does not receive the previous account's notifications.
 */
export async function unregisterPush(): Promise<void> {
  const push = await plugin();
  if (!push) return;
  try {
    const { value } = await new Promise<{ value: string | null }>((resolve) => {
      let done = false;
      void push.addListener('registration', (t) => {
        if (!done) {
          done = true;
          resolve({ value: t?.value || null });
        }
      });
      setTimeout(() => {
        if (!done) {
          done = true;
          resolve({ value: null });
        }
      }, 3000);
    });
    if (value) {
      await authFetch(`${API_BASE}/api/push/unregister`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: value }),
      }).catch(() => {});
    }
    registered = false;
    await push.removeAllListeners().catch(() => {});
  } catch {
    /* best effort */
  }
}

/**
 * Listen for a notification being tapped. Returns a cleanup function.
 *
 * Also fires for `pushNotificationReceived`: a notification that lands while the
 * app is already open shows nothing on iOS, so the app has to react to it itself
 * or a read that finished while the user sat on the screen appears to hang.
 */
export async function onPushOpened(handler: (data: PushData) => void): Promise<() => void> {
  const push = await plugin();
  if (!push) return () => {};

  const listeners: { remove: () => Promise<void> }[] = [];
  try {
    listeners.push(
      await push.addListener('pushNotificationActionPerformed', (action) => {
        handler((action?.notification?.data ?? {}) as PushData);
      }),
    );
    listeners.push(
      await push.addListener('pushNotificationReceived', (notification) => {
        handler((notification?.data ?? {}) as PushData);
      }),
    );
  } catch (e) {
    console.error('[push] could not attach listeners:', e);
  }

  return () => {
    for (const l of listeners) void l.remove();
  };
}
